<template>
  <div class="cbs px-3 px-md-4 py-3">
    <!-- Nouvelle consigne -->
    <h4 class="cbs-title">{{ $t('lang.client_booking_signal.new_title') }}</h4>
    <p class="cbs-sub">
      {{ $t('lang.client_booking_signal.new_subtitle') }}
    </p>

    <div class="card cbs-form-card mb-4">
      <div class="card-body">
        <div class="mb-2">
          <button
            v-for="opt in typeOptions"
            :key="opt.value"
            type="button"
            class="cbs-pill"
            :class="[
              `cbs-pill--${opt.value}`,
              { 'cbs-pill--on': form.type === opt.value },
            ]"
            @click="form.type = opt.value"
          >
            <span class="cbs-pill-dot" :style="{ background: opt.color }"></span>
            {{ opt.label }}
          </button>
        </div>

        <div class="row">
          <div class="col-md-8 mb-3">
            <label class="cbs-label">
              {{ $t('lang.client_booking_signal.message_label') }} <em>*</em>
            </label>
            <textarea
              v-model="form.message"
              class="form-control"
              rows="3"
              maxlength="5000"
            ></textarea>
          </div>
          <div class="col-md-4 mb-3">
            <label class="cbs-label">
              {{ $t('lang.client_booking_signal.valid_until_label') }} <em>*</em>
            </label>
            <input
              v-model="form.valid_until"
              type="date"
              class="form-control"
              :min="minDate"
            />
            <small class="form-text text-muted">
              {{ $t('lang.client_booking_signal.valid_until_help') }}
            </small>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving || !canSave"
            @click="saveSignal"
          >
            {{
              saving
                ? $t('lang.client_booking_signal.saving')
                : $t('lang.client_booking_signal.save')
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Consigne active -->
    <h4 class="cbs-title">{{ $t('lang.client_booking_signal.active_title') }}</h4>
    <p class="cbs-sub">
      {{ $t('lang.client_booking_signal.active_subtitle') }}
    </p>

    <div v-if="loading" class="text-muted mb-4">
      {{ $t('lang.client_booking_signal.loading') }}
    </div>

    <div
      v-else-if="current"
      class="cbs-active-card mb-4"
      :class="`cbs-active-card--${current.type}`"
    >
      <span class="cbs-btag" :class="`cbs-btag--${current.type}`">
        {{ typeLabel(current.type) }}
      </span>
      <div class="flex-grow-1">
        <div class="cbs-active-msg">{{ current.message }}</div>
        <div class="cbs-active-meta">
          {{
            $t('lang.client_booking_signal.active_meta', {
              author: authorName(current),
              datetime: formatDateTime(current.created),
              date: formatDate(current.valid_until),
            })
          }}
        </div>
      </div>
      <button
        type="button"
        class="btn btn-link cbs-deactivate-btn p-0"
        :disabled="deactivating"
        @click="confirmDeactivate"
      >
        {{ $t('lang.client_booking_signal.deactivate') }}
      </button>
    </div>

    <div v-else class="alert alert-light border mb-4 mb-md-4">
      {{ $t('lang.client_booking_signal.empty') }}
    </div>

    <!-- Historique -->
    <h4 class="cbs-title mt-2">{{ $t('lang.button.history') }}</h4>
    <p class="cbs-sub">
      {{ $t('lang.client_booking_signal.history_subtitle') }}
    </p>

    <div v-if="!loading && (!history || history.length === 0)" class="text-muted">
      {{ $t('lang.client_booking_signal.empty') }}
    </div>

    <div v-else-if="history && history.length" class="table-responsive">
      <table class="table table-sm cbs-table mb-0">
        <thead>
          <tr>
            <th>{{ $t('lang.client_booking_signal.col_type') }}</th>
            <th>{{ $t('lang.label.message') }}</th>
            <th>{{ $t('lang.client_booking_signal.col_posed_by') }}</th>
            <th>{{ $t('lang.client_booking_signal.col_on') }}</th>
            <th>{{ $t('lang.client_booking_signal.col_until') }}</th>
            <th>{{ $t('lang.label.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in history" :key="row.id">
            <td>
              <span class="cbs-tb" :class="`cbs-tb--${row.type}`">
                {{ typeShortLabel(row.type) }}
              </span>
            </td>
            <td class="cbs-msg-cell">{{ row.message }}</td>
            <td>{{ authorName(row) }}</td>
            <td>{{ formatDate(row.created) }}</td>
            <td>{{ formatDate(row.valid_until) }}</td>
            <td>
              <span class="cbs-st" :class="`cbs-st--${row.status}`">
                {{ statusLabel(row.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { clientBookingSignalService } from '@/_services'

const TYPE_COLORS = {
  boost: '#dc3545',
  reduce: '#fd7e14',
  stop: '#343a40',
}

export default {
  name: 'ClientBookingSignal',
  props: {
    client_id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      deactivating: false,
      current: null,
      history: [],
      form: {
        type: 'boost',
        message: '',
        valid_until: '',
      },
    }
  },
  computed: {
    typeOptions() {
      return [
        {
          value: 'boost',
          label: this.$t('lang.client_booking_signal.type_boost'),
          color: TYPE_COLORS.boost,
        },
        {
          value: 'reduce',
          label: this.$t('lang.client_booking_signal.type_reduce'),
          color: TYPE_COLORS.reduce,
        },
        {
          value: 'stop',
          label: this.$t('lang.client_booking_signal.type_stop'),
          color: TYPE_COLORS.stop,
        },
      ]
    },
    minDate() {
      return this.todayYmd()
    },
    canSave() {
      return (
        !!this.form.type &&
        !!this.form.message &&
        this.form.message.trim() !== '' &&
        !!this.form.valid_until
      )
    },
  },
  watch: {
    client_id: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadSignals()
        }
      },
    },
  },
  methods: {
    todayYmd() {
      const d = new Date()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    },
    resetForm() {
      this.form = {
        type: 'boost',
        message: '',
        valid_until: '',
      }
    },
    loadSignals() {
      if (!this.client_id) return
      this.loading = true
      clientBookingSignalService
        .getBookingSignals(this.client_id)
        .then((res) => {
          this.current = res && res.current ? res.current : null
          this.history = res && res.history ? res.history : []
        })
        .catch((err) => {
          this.current = null
          this.history = []
          this.$swal({
            type: 'error',
            title: this.$t('lang.label.error'),
            text: this.errorText(err),
            confirmButtonColor: '#3085d6',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveSignal() {
      if (!this.canSave || this.saving) return

      if (this.form.valid_until < this.minDate) {
        this.$swal({
          type: 'warning',
          title: this.$t('lang.client_booking_signal.swal_invalid_date_title'),
          text: this.$t('lang.client_booking_signal.swal_invalid_date_text'),
          confirmButtonColor: '#3085d6',
        })
        return
      }

      this.saving = true
      const payload = {
        client_id: this.client_id,
        type: this.form.type,
        message: this.form.message.trim(),
        valid_until: this.form.valid_until,
      }

      clientBookingSignalService
        .createBookingSignal(payload)
        .then(() => {
          this.resetForm()
          this.loadSignals()
          this.$swal({
            type: 'success',
            title: this.$t('lang.client_booking_signal.swal_saved_title'),
            showConfirmButton: false,
            timer: 2000,
          })
        })
        .catch((err) => {
          this.$swal({
            type: 'error',
            title: this.$t('lang.label.error'),
            text: this.errorText(err),
            confirmButtonColor: '#3085d6',
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    confirmDeactivate() {
      if (!this.current || this.deactivating) return

      this.$swal({
        type: 'warning',
        title: this.$t('lang.client_booking_signal.swal_deactivate_title'),
        text: this.$t('lang.client_booking_signal.swal_deactivate_text'),
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33a2c',
        confirmButtonText: this.$t('lang.button.yes'),
        cancelButtonText: this.$t('lang.button.cancel'),
      }).then((result) => {
        if (result.value) {
          this.deactivateSignal()
        }
      })
    },
    deactivateSignal() {
      this.deactivating = true
      clientBookingSignalService
        .deactivateBookingSignal(this.client_id)
        .then(() => {
          this.loadSignals()
          this.$swal({
            type: 'success',
            title: this.$t('lang.client_booking_signal.swal_deactivated_title'),
            showConfirmButton: false,
            timer: 2000,
          })
        })
        .catch((err) => {
          this.$swal({
            type: 'error',
            title: this.$t('lang.label.error'),
            text: this.errorText(err),
            confirmButtonColor: '#3085d6',
          })
        })
        .finally(() => {
          this.deactivating = false
        })
    },
    authorName(row) {
      if (!row) return ''
      const name = [row.name, row.lastname].filter(Boolean).join(' ')
      return name || '—'
    },
    typeLabel(type) {
      const map = {
        boost: 'lang.client_booking_signal.type_boost',
        reduce: 'lang.client_booking_signal.type_reduce',
        stop: 'lang.client_booking_signal.type_stop',
      }
      return map[type] ? this.$t(map[type]) : type
    },
    typeShortLabel(type) {
      const map = {
        boost: 'lang.client_booking_signal.type_boost_short',
        reduce: 'lang.client_booking_signal.type_reduce_short',
        stop: 'lang.client_booking_signal.type_stop_short',
      }
      return map[type] ? this.$t(map[type]) : type
    },
    statusLabel(status) {
      const map = {
        active: 'lang.client_booking_signal.status_active',
        replaced: 'lang.client_booking_signal.status_replaced',
        expired: 'lang.client_booking_signal.status_expired',
        deactivated: 'lang.client_booking_signal.status_deactivated',
      }
      return map[status] ? this.$t(map[status]) : status
    },
    formatDate(value) {
      if (!value) return '—'
      const raw = String(value).substring(0, 10)
      const parts = raw.split('-')
      if (parts.length !== 3) return raw
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatDateTime(value) {
      if (!value) return '—'
      const str = String(value)
      const datePart = this.formatDate(str)
      const timeMatch = str.match(/(\d{2}):(\d{2})/)
      if (timeMatch) {
        return this.$t('lang.client_booking_signal.datetime_at', {
          date: datePart,
          time: `${timeMatch[1]}:${timeMatch[2]}`,
        })
      }
      return datePart
    },
    errorText(err) {
      if (!err) return this.$t('lang.client_booking_signal.generic_error')
      if (typeof err === 'string') return err
      if (err.message) return err.message
      if (err.error) return err.error
      return this.$t('lang.client_booking_signal.generic_error')
    },
  },
}
</script>

<style scoped>
.cbs-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 3px;
  color: #1a1d29;
}

.cbs-sub {
  font-size: 11.5px;
  color: #6c757d;
  margin-bottom: 12px;
}

.cbs-form-card {
  border: 1px solid #e6e8ef;
  border-radius: 8px;
  background: #fdfdff;
}

.cbs-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}

.cbs-label em {
  color: #dc3545;
  font-style: normal;
}

.cbs-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 14px;
  border: 1.5px solid #e6e8ef;
  color: #4a4f63;
  margin: 0 6px 8px 0;
  background: #fff;
  cursor: pointer;
}

.cbs-pill-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.cbs-pill--boost.cbs-pill--on {
  border-color: #dc3545;
  color: #dc3545;
  background: #fdf1f2;
}

.cbs-pill--reduce.cbs-pill--on {
  border-color: #fd7e14;
  color: #fd7e14;
  background: #fff5eb;
}

.cbs-pill--stop.cbs-pill--on {
  border-color: #343a40;
  color: #343a40;
  background: #f1f2f5;
}

.cbs-active-card {
  border-left: 4px solid #dc3545;
  background: #fdf1f2;
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.cbs-active-card--reduce {
  border-left-color: #fd7e14;
  background: #fff5eb;
}

.cbs-active-card--stop {
  border-left-color: #343a40;
  background: #f1f2f5;
}

.cbs-btag {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 3px;
  color: #fff;
  background: #dc3545;
  white-space: nowrap;
  margin-top: 2px;
}

.cbs-btag--reduce {
  background: #fd7e14;
}

.cbs-btag--stop {
  background: #343a40;
}

.cbs-active-msg {
  font-weight: 600;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
}

.cbs-active-meta {
  font-size: 11.5px;
  color: #6c757d;
  margin-top: 3px;
}

.cbs-deactivate-btn {
  font-size: 11.5px;
  color: #5155ea !important;
  font-weight: 600;
  white-space: nowrap;
  margin-top: 4px;
  text-decoration: none;
}

.cbs-table {
  font-size: 12.5px;
}

.cbs-table th {
  background: #f4f5f8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4a4f63;
  border-bottom: 1px solid #e6e8ef;
  white-space: nowrap;
}

.cbs-msg-cell {
  color: #3c4152;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 420px;
}

.cbs-tb {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 3px;
  color: #fff;
  white-space: nowrap;
}

.cbs-tb--boost {
  background: #dc3545;
}

.cbs-tb--reduce {
  background: #fd7e14;
}

.cbs-tb--stop {
  background: #343a40;
}

.cbs-st {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

.cbs-st--active {
  color: #0f7a3d;
  background: #e7f6ee;
}

.cbs-st--expired {
  color: #6c757d;
  background: #eef0f3;
}

.cbs-st--replaced {
  color: #8a5a00;
  background: #fdf3e0;
}

.cbs-st--deactivated {
  color: #8a90a0;
  background: #f1f2f5;
}
</style>
